/**
 * DesertBiome - The first biome (0-500 miles)
 *
 * Desert characteristics:
 * - Gentle, rolling sand dunes
 * - Wide, sweeping curves
 * - Sparse vegetation with desert shrubs and rocks
 * - Warm earth-tone color palette (no greens)
 */

import { BiomeRenderer } from '../BiomeRenderer.js';
import { createBiomeConfig, validateBiomeConfig } from './BiomeConfig.js';

// Desert-specific color palettes for each time of day
// Index: 0=Day, 1=Sunset, 2=Night, 3=Dawn
export const DESERT_COLORS = [
    // ═══════════════════════════════════════════════════════════════════════════
    // DAY - Warm desert earth tones
    // ═══════════════════════════════════════════════════════════════════════════
    {
        terrainColors: [
            // Warm earth/sand tones
            0xc4a574, 0xb8a076, 0xd4b896,
            // Desert tan/ochre
            0xd4a06a, 0xc9956b, 0xbe8a5c,
            // Dry sand/sage brown
            0xbdb76b, 0xc9ba82, 0xa09060
        ],
        rockColors: [
            0x8b8378,   // Warm gray granite
            0xa09080,   // Sandstone
            0x9c8c7c,   // Weathered stone
            0x7a7068    // Cool brown rock
        ],
        shrubColors: [
            0x6b7e4e,   // Dusty sage
            0x7d7a3d,   // Desert olive
            0x8a8b5a,   // Dried sage
            0x8b7d5a    // Dried brush
        ]
    },

    // ═══════════════════════════════════════════════════════════════════════════
    // SUNSET - Golden hour desert
    // ═══════════════════════════════════════════════════════════════════════════
    {
        terrainColors: [
            // Golden/amber lit terrain
            0xd4a06a, 0xc9956b, 0xbe8a5c,
            // Warm shadows
            0xa07858, 0x9c7050, 0x8b6548,
            // Highlighted peaks
            0xe8c090, 0xddb580, 0xc9a070
        ],
        rockColors: [
            0xa07560,   // Terracotta
            0x9b6b55,   // Warm clay
            0x8b6050,   // Adobe
            0x7a5545    // Shadowed rust
        ],
        shrubColors: [
            0x6b5540,   // Warm brown
            0x5a4a38,   // Shadowed olive
            0x7a6048,   // Bronze
            0x4d4030    // Deep shadow
        ]
    },

    // ═══════════════════════════════════════════════════════════════════════════
    // NIGHT - Moonlit desert blues
    // ═══════════════════════════════════════════════════════════════════════════
    {
        terrainColors: [
            // Moonlit blue-grays
            0x586878, 0x5e6e7e, 0x546470,
            // Cool dark earth
            0x4c5660, 0x525c68, 0x485260,
            // Subtle purple tints
            0x565868, 0x585a6a, 0x505260
        ],
        rockColors: [
            0x4a4a58,   // Dark blue-gray
            0x454550,   // Night stone
            0x505060,   // Moonlit granite
            0x484850    // Shadow rock
        ],
        shrubColors: [
            0x181c20,   // Very dark
            0x1a1e24,   // Dark night
            0x161a1e,   // Deep shadow
            0x1c2228    // Dark blue-tinted
        ]
    },

    // ═══════════════════════════════════════════════════════════════════════════
    // DAWN - Soft morning desert
    // ═══════════════════════════════════════════════════════════════════════════
    {
        terrainColors: [
            // Gentle morning earth tones
            0xb8a888, 0xc0b090, 0xa89878,
            // Soft morning sand
            0xc8b898, 0xd0c0a0, 0xb8a080,
            // Misty pastels
            0xc0b8a8, 0xb8b0a0, 0xc8c0b0
        ],
        rockColors: [
            0x9a9088,   // Pale granite
            0xa89888,   // Morning sandstone
            0x8a8078,   // Cool morning stone
            0x908578    // Soft gray
        ],
        shrubColors: [
            0x706858,   // Morning brown
            0x605848,   // Soft taupe
            0x7a7260,   // Dusty
            0x686050    // Muted brown
        ]
    }
];

export class DesertBiome extends BiomeRenderer {
    constructor(scene, camera, worldTranslator, config = {}) {
        // Desert-specific configuration
        const desertConfig = createBiomeConfig({
            // ─────────────────────────────────────────────────────────────
            // ROAD - Desert highway with sweeping curves
            // Uses hash-based noise with prime intervals for non-repeating paths
            // ─────────────────────────────────────────────────────────────
            roadWidth: 10,
            chunkLength: 50,
            pathSegmentLength: 150,          // Spline control point spacing
            pathLookAhead: 100,
            roadMaxLateralOffset: 250,       // Wide sweeping desert curves
            roadMaxVerticalOffset: 35,       // Gentle desert hills
            roadSplineTension: 0.0,          // Zero tension = tight spline following

            // ─────────────────────────────────────────────────────────────
            // TERRAIN - Desert landscape: mostly flat with occasional features
            // ─────────────────────────────────────────────────────────────
            terrainColor: 0x5d4e6d,          // Desert purple/brown
            terrainAmplitudeLarge: 180,      // Reduced - occasional distant mountains
            terrainAmplitudeMedium: 60,      // Reduced - gentle rolling hills
            terrainAmplitudeSmall: 20,       // Reduced - subtle undulation
            terrainAmplitudeFine: 5,         // Minimal fine detail
            terrainHeightMultiplier: 1.0,
            waterLevel: -31,  // Oasis lakes in deep valleys only

            // ─────────────────────────────────────────────────────────────
            // TERRAIN DIMENSIONS - Balance visibility vs performance
            // ─────────────────────────────────────────────────────────────
            terrainWidth: 800,               // Total width of terrain on each side of road (units) - reduced for performance
            terrainSegmentsOut: 20,          // Segments perpendicular to road (higher = smoother)
            terrainSegmentsAlong: 1,         // Segments along road direction (1 = low-poly chunk-wide faces)

            // ─────────────────────────────────────────────────────────────
            // ROADSIDE STRIP - High-res terrain bridging road to mountains
            // This strip follows road curves precisely, preventing gaps during turns
            // ─────────────────────────────────────────────────────────────
            roadsideStripWidth: 20,          // Width of high-res strip next to road (units)
            roadsideStripSegments: 4,        // Segments perpendicular within strip (higher = smoother)
            roadsideStripOverlap: 0.02,      // How far strip extends under road edge (prevents gaps)
            mountainOverlapIntoStrip: 10,     // How far mountains extend into strip (hides seams)

            // Desert-appropriate terrain features
            // Mostly flat with gentle dunes, rare dramatic features
            terrainFeaturesEnabled: true,
            terrainFeatureScale: 0.00004,    // Larger, more spread out feature regions
            terrainFeatureWeights: {
                dunes: 1.5,       // Rolling sand dunes - dominant feature
                peaks: 0.15,      // Rare distant mountain silhouettes
                plateaus: 0.7,    // Occasional flat-topped mesas
                spires: 0.08,     // Very rare rock formations
                cliffs: 0.25,     // Occasional canyon walls
                valleys: 0.6      // Dry washes and arroyos - common
            },
            terrainFeatureBlend: 0.5,        // Smoother transitions

            // ─────────────────────────────────────────────────────────────
            // TERRAIN HEIGHT LAYERING - Elevation increases with distance from road
            // Layers: near-road, mid-distance, far, horizon
            // Scaled for 800 unit terrain width (was 2000)
            // ─────────────────────────────────────────────────────────────
            terrainLayerHeights: [0.35, 0.6, 1.0, 1.5],      // Height multiplier per layer (0-1 normalized)
            terrainLayerDistances: [0.15, 0.45, 1.0, 1.0],   // Distance thresholds scaled for narrower terrain

            // ─────────────────────────────────────────────────────────────
            // FLAT ZONE - Gap between road edge and where terrain starts rising
            // Controls how close mountains/terrain can get to the road
            // Scaled for 800 unit terrain width (was 2000)
            // ─────────────────────────────────────────────────────────────
            terrainFlatZoneMin: 0.008,       // Minimum flat distance (fraction of width)
            terrainFlatZoneMax: 0.18,        // Maximum flat distance (fraction of width)
            terrainBlendRange: 0.15,         // How gradually terrain rises from flat zone (larger = smoother)

            // ─────────────────────────────────────────────────────────────
            // ROADSIDE OBJECTS - Sparse desert vegetation and rocks
            // ─────────────────────────────────────────────────────────────
            enableRoadsideObjects: true,
            roadsideObjectSpawnDistance: 700,
            roadsideObjectDensity: 0.3,      // Sparse (it's a desert)
            roadsideObjectMinSafeZone: 10,
            roadsideObjectDistributionWidth: 200,
            rockProbability: 0.6,            // More rocks than shrubs
            rockMinSize: 2,
            rockMaxSize: 7,
            shrubMinSize: 3,
            shrubMaxSize: 10,
            shrubSpriteCount: 3,
            shrubScaleVariation: 0.3,
            shrubStretchVariation: 0.3,
            roadsideObjectFadeInDuration: 0.5,
            shrubSpritePaths: [
                'assets/sprites/shrub1.png',
                'assets/sprites/shrub2.png',
                'assets/sprites/shrub3.png',
                'assets/sprites/shrub4.png',
                'assets/sprites/shrub5.png'
            ],

            // Merge with any user-provided config
            ...config
        });

        // Validate configuration in development
        validateBiomeConfig(desertConfig, 'DesertBiome');

        super(scene, camera, worldTranslator, desertConfig);

        // Biome identity
        this.biomeName = 'Desert';
        this.minMiles = 0;
        this.maxMiles = 500;

        // Store biome-specific color palettes (via palette manager)
        this.biomeColors = DESERT_COLORS;
        this.palette.setBiomeColors(DESERT_COLORS);
    }

    /**
     * Get terrain colors for a specific time of day
     * @param {number} timeIndex - 0=Day, 1=Sunset, 2=Night, 3=Dawn
     * @returns {number[]} Array of terrain color hex values
     */
    getTerrainColors(timeIndex) {
        const palette = this.biomeColors[timeIndex];
        return palette ? palette.terrainColors : [];
    }

    /**
     * Get rock colors for a specific time of day
     * @param {number} timeIndex - 0=Day, 1=Sunset, 2=Night, 3=Dawn
     * @returns {number[]} Array of rock color hex values
     */
    getRockColors(timeIndex) {
        const palette = this.biomeColors[timeIndex];
        return palette ? palette.rockColors : [];
    }

    /**
     * Get shrub colors for a specific time of day
     * @param {number} timeIndex - 0=Day, 1=Sunset, 2=Night, 3=Dawn
     * @returns {number[]} Array of shrub color hex values
     */
    getShrubColors(timeIndex) {
        const palette = this.biomeColors[timeIndex];
        return palette ? palette.shrubColors : [];
    }

    // Desert biome uses default BiomeRenderer behavior
    // Override methods here to add desert-specific features like:
    // - Dust devils
    // - Heat shimmer/mirage effects
    // - Tumbleweeds
}

/**
 * Export biome registry entry for easy registration
 */
export const DESERT_BIOME_ENTRY = {
    minMiles: 0,
    maxMiles: 500,
    BiomeClass: DesertBiome,
    name: 'Desert'
};
